import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ErrorPanel } from "../../shell/ErrorPanel";
import { RacesApi, type Race } from "../../admin/races/api";
import { PVTApi, type PVTResult } from "./api";

/**
 * PVT list — recent captures with turnout %, leading candidate, and the
 * project-wide totals for the selected race+period. Aggregation is
 * intentionally simple for the MVP: sum candidate votes across the visible
 * stations and rank them. Sampling-frame projection comes later.
 */
export function PVTList({ period }: { period: string }) {
  const [raceId, setRaceId] = useState("");
  const races = useQuery({ queryKey: ["races"], queryFn: RacesApi.list });

  const results = useQuery({
    queryKey: ["pvt-results", { raceId, period }],
    queryFn: () => PVTApi.list({ raceId: raceId || undefined, period }),
  });

  // Aggregate across visible stations for the chosen race.
  const aggregate = useMemo(() => {
    const items = results.data?.items ?? [];
    const byCandidate = new Map<string, { name: string; party: string; votes: number }>();
    let stations = 0;
    let totalValid = 0;
    let totalInvalid = 0;
    let totalRegistered = 0;
    for (const r of items) {
      stations += 1;
      totalValid += r.validVotes ?? 0;
      totalInvalid += r.invalidVotes ?? 0;
      totalRegistered += r.registeredVoters ?? 0;
      for (const c of r.candidates ?? []) {
        const key = `${(c.name || "").toLowerCase()}__${(c.party || "").toLowerCase()}`;
        const cur = byCandidate.get(key) ?? { name: c.name, party: c.party, votes: 0 };
        cur.votes += c.votes ?? 0;
        byCandidate.set(key, cur);
      }
    }
    const ranked = Array.from(byCandidate.values()).sort((a, b) => b.votes - a.votes);
    const total = totalValid + totalInvalid;
    return {
      stations, totalValid, totalInvalid, totalRegistered, total,
      ranked,
      turnoutPct: totalRegistered > 0 ? (total / totalRegistered) * 100 : 0,
    };
  }, [results.data]);

  const leadByStation = (r: PVTResult): { name: string; pct: number } | null => {
    const top = (r.candidates ?? []).slice().sort((a, b) => (b.votes || 0) - (a.votes || 0))[0];
    if (!top) return null;
    const denom = r.validVotes || 1;
    return { name: top.name, pct: ((top.votes || 0) / denom) * 100 };
  };

  return (
    <div>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>PVT — Parallel Vote Tabulation</h2>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Polling-station tally captures by intelligence officers. Period: <strong>{period}</strong>.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 11, color: "var(--muted)" }}>
            Race{" "}
            <select value={raceId} onChange={(e) => setRaceId(e.target.value)} style={{ marginLeft: 4 }}>
              <option value="">All</option>
              {races.data?.items.map((r: Race) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <Link to="pvt/new" className="ghost" style={{ padding: "6px 12px" }}>+ Capture PVT</Link>
        </div>
      </header>

      {results.error && <ErrorPanel err={results.error} title="Couldn't load PVT results" />}

      {/* Aggregate strip */}
      <div className="row-3" style={{ gap: 12, marginBottom: 12 }}>
        <Stat label="Stations reporting" value={aggregate.stations.toLocaleString()} />
        <Stat
          label="Total votes counted"
          value={`${aggregate.total.toLocaleString()}${aggregate.totalRegistered > 0 ? ` (${aggregate.turnoutPct.toFixed(1)}% turnout)` : ""}`}
        />
        <Stat label="Invalid" value={aggregate.totalInvalid.toLocaleString()} />
      </div>

      {/* Ranked candidates */}
      {aggregate.ranked.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <div className="card-title">
            Leading candidates {raceId && races.data?.items.find((r) => r.id === raceId) && (
              <span className="muted" style={{ fontWeight: 400 }}>
                — {races.data.items.find((r) => r.id === raceId)?.name}
              </span>
            )}
          </div>
          <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
            Sum across the <strong>{aggregate.stations}</strong> stations you can see.
            Not a national projection — sampling-frame extrapolation is a future feature.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                <th style={{ padding: "4px 8px" }}>Rank</th>
                <th style={{ padding: "4px 8px" }}>Candidate</th>
                <th style={{ padding: "4px 8px" }}>Party</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Votes</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>%</th>
              </tr>
            </thead>
            <tbody>
              {aggregate.ranked.slice(0, 12).map((c, i) => (
                <tr key={`${c.name}-${c.party}`} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "4px 8px", color: "var(--muted)" }}>{i + 1}</td>
                  <td style={{ padding: "4px 8px", fontWeight: i === 0 ? 600 : 400 }}>{c.name}</td>
                  <td style={{ padding: "4px 8px", color: "var(--muted)" }}>{c.party || "—"}</td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>{c.votes.toLocaleString()}</td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    {aggregate.totalValid > 0 ? `${((c.votes / aggregate.totalValid) * 100).toFixed(1)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-station list */}
      <div className="card" style={{ padding: 16 }}>
        <div className="card-title">Recent captures</div>
        {results.isLoading && <p className="muted" style={{ fontSize: 13 }}>Loading…</p>}
        {!results.isLoading && (results.data?.items.length ?? 0) === 0 && (
          <p className="muted" style={{ fontSize: 13 }}>
            No PVT captures yet for this race / period. Click <strong>+ Capture PVT</strong> to add one.
          </p>
        )}
        {(results.data?.items.length ?? 0) > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 8 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                <th style={{ padding: "4px 8px" }}>Polling station</th>
                <th style={{ padding: "4px 8px" }}>Race</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Valid</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Invalid</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Turnout</th>
                <th style={{ padding: "4px 8px" }}>Lead</th>
              </tr>
            </thead>
            <tbody>
              {(results.data?.items ?? []).map((r) => {
                const lead = leadByStation(r);
                const race = races.data?.items.find((x) => x.id === r.raceId);
                return (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "4px 8px" }}><code>{r.pollingStationId}</code></td>
                    <td style={{ padding: "4px 8px" }}>{race?.name ?? r.raceId}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>{r.validVotes?.toLocaleString() ?? "—"}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>{r.invalidVotes?.toLocaleString() ?? "—"}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>
                      {r.turnoutPct ? `${r.turnoutPct.toFixed(1)}%` : "—"}
                    </td>
                    <td style={{ padding: "4px 8px" }}>
                      {lead ? `${lead.name} (${lead.pct.toFixed(1)}%)` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}
